import { Box, Button, Card, CardContent, Chip, CircularProgress, Collapse, Grid, IconButton, Stack, TextField, Tooltip, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import Chart from "react-google-charts";
import { BusinessCenter, ChevronLeft, ChevronRight, EventNote, Groups, People, PieChartOutlined, Replay } from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faListCheck } from '@fortawesome/free-solid-svg-icons';
import { options, optionsPieChart } from "../optionsChart";
import {
  getClientCount,
  getClientsPieData,
  getJobsWeeklyStatistics,
  getProjectCount,
  getUserCount,
} from "../../../../api/apiCall";
import { useAuth } from "../../../../auth/auth";
import { useNavigate } from "react-router-dom";
import { format } from 'date-fns';
import { toast } from "react-toastify";
import { useUKRailWeek } from "../../../../hooks/useUKRailWeek";
import { useRailCalendar } from "../../../../hooks/useRailCalendar";
import styles from './Dashboard.module.css';

const AdminDashboard = () => {
    const {state} = useAuth();
    const navigate = useNavigate();
    const { currentWeek, currentYear } = useUKRailWeek();
    const [userCount, setUserCount] = useState(0);
    const [clientCount, setClientCount] = useState(0);
    const [projectCount, setProjectCount] = useState(0);
    const [statsLoading, setStatsLoading] = useState(true);
    const [chartsLoading, setChartsLoading] = useState(true);
    const [clientPieData, setClientPieData] = useState([]);
    const [jobStatistics, setJobStatistics] = useState<any>(undefined);
    const [week, setWeek] = useState(currentWeek);
    const [year, setYear] = useState(Number(currentYear));
    const [weekInput, setWeekInput] = useState(currentWeek);
    const [yearInput, setYearInput] = useState(Number(currentYear));
    const [jumpOpen, setJumpOpen] = useState(false);
    const { weeks, loading: railLoading } = useRailCalendar(state.user?.token, year);
    const selectedWeek = railLoading ? undefined : weeks.find((w) => w.weekNumber === week);
    const maxWeek = weeks.length || 53;

    const getClientsPieDatas = async () => {
        try {
            const response = await getClientsPieData(state.user?.token, week, year)
            setClientPieData(response?.data || [])
        } catch (error) {
            console.log(error)
            toast.error('Failed to load the clients report');
        }
    }

    const getJobsStatistics = async () => {
        try {
            const response = await getJobsWeeklyStatistics(state.user?.token, week, year)
            setJobStatistics(response);
        } catch (error) {
            console.log(error)
            toast.error('Failed to load the shifts report');
      }
    }

    useEffect(() => {
        setChartsLoading(true);
        Promise.allSettled([getJobsStatistics(), getClientsPieDatas()]).finally(() => setChartsLoading(false));
    }, [week, year]);

    const days = ['SAT', 'SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI'];

    const data = [
      ["Day", "Shifts", { role: "annotation" }],
      ...days.map((day, i) => [
        day,
        jobStatistics ? jobStatistics[i] : undefined,
        jobStatistics ? jobStatistics[i] : undefined,
      ]),
    ];

    const totalShifts = jobStatistics
        ? days.reduce((sum, _day, i) => sum + (Number(jobStatistics[i]) || 0), 0)
        : 0;

    const pieData: any = clientPieData.map(({ name, value }) => [name, value]);
    pieData.unshift(["Clients", "Shifts"]);

    const hasPieData = pieData.length > 1;

    const stats = [
        {
          value: userCount,
          label: "Users",
          icon: <People sx={{ fontSize: 26 }} />,
          color: "#06b6d4",
          url: '/pace-team/resourcing/staff-details',
        },
        {
          value: clientCount,
          label: "Clients",
          icon: <BusinessCenter sx={{ fontSize: 26 }} />,
          color: "#8b5cf6",
          url: '/pace-team/collaborations/clients',
        },
        {
          value: projectCount,
          label: "Projects",
          icon: <FontAwesomeIcon icon={faListCheck} style={{ fontSize: 20 }} />,
          color: "#f59e0b",
          url: '/pace-team/collaborations/projects-overview',
        },
    ];

    const getUserCounts = async () => {
        try {
            const response = await getUserCount(state.user?.token);
            setUserCount(response)
        } catch (error) {
            console.log(error)
        }
    }

    const getClientCounts = async () => {
        try {
            const response = await getClientCount(state.user?.token);
            setClientCount(response)
        } catch (error) {
            console.log(error)
        }
    }

    const getProjectCounts = async () => {
        try {
            const response = await getProjectCount(state.user?.token);
            setProjectCount(response)
        } catch (error) {
            console.log(error)
        }
    }

    useEffect(() => {
        Promise.allSettled([getUserCounts(), getClientCounts(), getProjectCounts()]).finally(() => setStatsLoading(false));
    }, []);

    const handleNavigate = async (url: any) => {
        navigate(url);
    };

    const isCurrentWeek = week === currentWeek && year === Number(currentYear);

    const applyFilter = () => {
        const w = Number(weekInput);
        const y = Number(yearInput);
        if (!w || w < 1 || w > maxWeek) {
            toast.warn(`Please enter a valid week (1-${maxWeek})`);
            return;
        }
        if (!y || y < 2000) {
            toast.warn('Please enter a valid year');
            return;
        }
        setWeek(w);
        setYear(y);
        setJumpOpen(false);
    };

    const goToCurrentWeek = () => {
        setWeekInput(currentWeek);
        setYearInput(Number(currentYear));
        setWeek(currentWeek);
        setYear(Number(currentYear));
    };

    const shiftWeek = (delta: number) => {
        let newWeek = week + delta;
        let newYear = year;
        if (newWeek < 1) { newWeek = 52; newYear -= 1; }
        if (newWeek > maxWeek) { newWeek = 1; newYear += 1; }
        setWeek(newWeek);
        setYear(newYear);
        setWeekInput(newWeek);
        setYearInput(newYear);
    };

    return (
        <Box sx={{ width: "100%", p: 2 }}>
            <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1 }}>
                <Box>
                    <Typography variant='h5' sx={{ fontWeight: "bold" }}>Dashboard</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Overview for week {week}, {year}
                    </Typography>
                </Box>
            </Stack>

            {/* Stat cards */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
                {stats.map((stat) => (
                    <Grid key={stat.label} size={{ xs: 12, sm: 4 }}>
                        <Card
                            variant="outlined"
                            className={styles.statsCard}
                            sx={{
                                borderRadius: 3,
                                border: "none",
                                transition: "box-shadow .15s ease, transform .15s ease",
                                "&:hover": { boxShadow: 1, transform: "translateY(-1px)" },
                            }}
                            onClick={() => handleNavigate(stat.url)}
                        >
                            <CardContent>
                                <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
                                    <Box
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: 2.5,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            backgroundColor: `${stat.color}1f`,
                                            color: stat.color,
                                            flexShrink: 0,
                                        }}
                                    >
                                        {stat.icon}
                                    </Box>
                                    <Box sx={{ flexGrow: 1 }}>
                                        <Typography variant="h5" sx={{ fontWeight: "bold", lineHeight: 1.2 }}>
                                            {statsLoading ? <CircularProgress size={20} /> : stat.value}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {stat.label}
                                        </Typography>
                                    </Box>
                                    <ChevronRight sx={{ color: "text.disabled" }} />
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

             {/* Week navigator */}
            <Card variant="outlined" sx={{ borderRadius: 3, mb: 2, border: "none" }}>
                <CardContent sx={{ pb: '16px !important' }}>
                    <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5 }}>
                        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                            <Tooltip title="Previous week">
                                <IconButton size="small" onClick={() => shiftWeek(-1)}>
                                    <ChevronLeft />
                                </IconButton>
                            </Tooltip>
                            <Box sx={{ minWidth: 260, textAlign: "center" }}>
                                <Stack direction="row" spacing={1} sx={{ alignItems: "center", justifyContent: "center" }}>
                                    <Typography sx={{ fontWeight: 600, color: "#5470c6" }}>
                                        Week {week}
                                    </Typography>
                                    {isCurrentWeek && <Chip size="small" label="Current" color="primary" variant="outlined" />}
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                    {selectedWeek ? format(selectedWeek.startDate, "dd MMM yyyy") : '-'}
                                    {" – "}
                                    {selectedWeek ? format(selectedWeek.endDate, "dd MMM yyyy") : '-'}
                                </Typography>
                            </Box>
                            <Tooltip title="Next week">
                                <IconButton size="small" onClick={() => shiftWeek(1)}>
                                    <ChevronRight />
                                </IconButton>
                            </Tooltip>
                        </Stack>

                        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                            {!isCurrentWeek && (
                                <Button size="small" color="inherit" startIcon={<Replay fontSize="small" />} onClick={goToCurrentWeek}>
                                    This week
                                </Button>
                            )}
                            <Button
                                size="small"
                                variant={jumpOpen ? "contained" : "outlined"}
                                startIcon={<EventNote fontSize="small" />}
                                style={{ boxShadow: "none" }}
                                onClick={() => setJumpOpen((o) => !o)}
                            >
                                Jump to week
                            </Button>
                        </Stack>
                    </Stack>

                    <Collapse in={jumpOpen}>
                        <Stack direction="row" spacing={1.5} sx={{ mt: 2, alignItems: "center", flexWrap: "wrap" }}>
                            <TextField
                                label="Week"
                                size="small"
                                type="number"
                                value={weekInput}
                                onChange={(e: any) => setWeekInput(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && applyFilter()}
                                sx={{ width: 120 }} />
                            <TextField
                                label="Year"
                                size="small"
                                type="number"
                                value={yearInput}
                                onChange={(e: any) => setYearInput(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && applyFilter()}
                                sx={{ width: 120 }} />
                            <Button
                                variant="contained"
                                size="small"
                                style={{ boxShadow: "none" }}
                                onClick={applyFilter}>
                                    Go
                            </Button>
                        </Stack>
                    </Collapse>
                </CardContent>
            </Card>

            {/* Charts */}
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 8 }}>
                    <Card variant="outlined" sx={{ borderRadius: 3, border: "none" }} className={styles.columnChartCard}>
                        <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                                <Groups sx={{ color: "#5470c6" }} />
                                <Box>
                                    <Typography sx={{ fontWeight: 600 }}>Shifts Report</Typography>
                                    <Typography variant="body2" color="text.secondary">Week {week}</Typography>
                                </Box>
                            </Stack>
                            {!chartsLoading && (
                                <Chip size="small" label={`${totalShifts} shifts`} sx={{ fontWeight: 600 }} />
                            )}
                        </Stack>
                        {chartsLoading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "450px" }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Chart
                                chartType="ColumnChart"
                                width="100%"
                                height="450px"
                                data={data}
                                options={options}
                            />
                        )}
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <Card variant="outlined" sx={{ borderRadius: 3, border: "none" }} className={styles.pieChartCard}>
                        <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                                <PieChartOutlined sx={{ color: "#5470c6" }} />
                                <Box>
                                    <Typography sx={{ fontWeight: 600 }}>Shifts by Client</Typography>
                                    <Typography variant="body2" color="text.secondary">Week {week}</Typography>
                                </Box>
                            </Stack>
                            {!chartsLoading && hasPieData && (
                                <Chip size="small" label={`${clientPieData.length} clients`} sx={{ fontWeight: 600 }} />
                            )}
                        </Stack>
                        {chartsLoading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "450px" }}>
                                <CircularProgress />
                            </Box>
                        ) : hasPieData ? (
                            <Chart
                                chartType="PieChart"
                                width="100%"
                                height="450px"
                                data={pieData}
                                options={optionsPieChart}
                            />
                        ) : (
                            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "450px", color: "text.secondary" }}>
                                <Typography variant="body2">No shifts for this week.</Typography>
                            </Box>
                        )}
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
}

export default AdminDashboard;
